import { createApp } from 'vue';
import { setup as DataDogSetup } from '@ilteducation/datadog';
import pinia from '@/plugins/pinia';
import i18n from '@/plugins/translation';
import stonlyPlugin from '@ilteducation/stonly';
import App from './App.vue';
import router from './router';
import interceptorsSetup from './foundations/helpers/interceptors';
import { safeStorage } from './foundations/helpers/version-check';
import { createHead } from '@unhead/vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
// Import optimized icon configuration
import '@/composables/use-icons';
import vuetify from './plugins/vuetify';
import useAppVersion from '@/composables/use-app-version';

const { appVersion } = useAppVersion();
interceptorsSetup();

const PRELOAD_RELOAD_KEY = 'manabua:preload-error-reloaded-version';
const tabStorage = safeStorage(() => sessionStorage);

// A deploy deletes the previous build's files, so a tab opened before it cannot load a file it has not fetched yet,
// such as a game's 3D engine. Reload onto the new build, at most once per build: while a deploy is still in progress
// the reload gets the old build again, and offline a reload would only show the browser's error page.
window.addEventListener('vite:preloadError', () => {
  if (!navigator.onLine || tabStorage.getItem(PRELOAD_RELOAD_KEY) === appVersion) {
    return;
  }
  tabStorage.setItem(PRELOAD_RELOAD_KEY, appVersion);
  window.location.reload();
});

if (!process.env.VITE_DISABLE_DATADOG) {
  DataDogSetup({
    applicationId: process.env.VITE_DD_APPLICATION_ID,
    clientToken: process.env.VITE_DD_CLIENT_TOKEN,

    environment: process.env.VITE_ENVIRONMENT!,

    service: process.env.VITE_DD_SERVICE!,
    sampleRate: process.env.VITE_ENVIRONMENT !== 'production' ? 100 : 10,
    version: appVersion,
  });
}

const app = createApp(App);

app.use(router);
app.use(pinia);
app.use(i18n);
app.use(createHead());

app.component('font-awesome-icon', FontAwesomeIcon);

if (import.meta.env.VITE_ENVIRONMENT === 'production' || import.meta.env.VITE_ENVIRONMENT === 'staging') {
  // only use in prod/staging
  app.use(stonlyPlugin, { wid: import.meta.env.VITE_STONLY_WID });
}

app.use(vuetify);
app.mount('#app');
